import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import TestimonialsSection from './components/TestimonialsSection';
import TESTIMONIALS from './data/testimonials';

const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_SM = '2px 2px 0px #1e1e1e';
const PRESSTO_PURPLE = '#5C4FB8';
const CREAM = '#F2EEE5';
const YELLOW = '#FFE082';

const WHATS_NEW = [
  {
    icon: '📰',
    title: 'Student newspapers',
    body: 'Classes now publish real newspapers — with student bylines, editions, and a shareable link. Writing has an audience.',
  },
  {
    icon: '🧩',
    title: 'Six structured writing plans',
    body: 'Opinion, Cause & Effect, Compare & Contrast, Narrative, Description, Problem & Solution. Every assignment has a plan that scaffolds the whole piece.',
  },
  {
    icon: '✨',
    title: 'AI Writing Buddy',
    body: 'Asks questions, prompts thinking, suggests words — but never writes for students. The work stays theirs.',
  },
  {
    icon: '🔍',
    title: 'Behavioral AI detection',
    body: 'Detects AI-generated writing through typing patterns and revision history — not just the text. Teachers see what actually happened.',
  },
  {
    icon: '📐',
    title: 'Curriculum-aligned lessons',
    body: 'Hundreds of ready-to-use writing activities aligned to the curricula your district already teaches.',
  },
  {
    icon: '🏫',
    title: 'Built for real classrooms',
    body: 'Google Classroom rostering, co-teacher access, per-student monitoring, and assignment handouts. Everything a teacher actually needs.',
  },
];

function CTAButton({ children, onClick, dark = true }: { children: React.ReactNode; onClick: () => void; dark?: boolean }) {
  return (
    <MuiButton
      variant="contained"
      onClick={onClick}
      sx={{
        backgroundColor: dark ? '#2C2C2C' : YELLOW,
        color: dark ? '#fff' : '#2C2C2C',
        fontFamily: 'Montserrat, Inter, sans-serif', fontWeight: 700, fontSize: '1rem',
        textTransform: 'none', borderRadius: '6px', border: '2px solid #1e1e1e',
        boxShadow: SHADOW, px: 4, py: 1.5,
        '&:hover': { boxShadow: SHADOW_SM, transform: 'translate(2px,2px)' },
        transition: 'all 100ms',
      }}
    >
      {children}
    </MuiButton>
  );
}

export default function WelcomeBackPage() {
  const navigate = useNavigate();

  return (
    <LandingLayout>
      {/* Hero */}
      <Box sx={{ backgroundColor: CREAM, borderBottom: '2px solid #1e1e1e', py: { xs: 8, md: 12 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1.5,
            backgroundColor: '#fff', border: '2px solid #1e1e1e', borderRadius: '6px',
            px: 2, py: 1, mb: 4, boxShadow: SHADOW_SM,
          }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: PRESSTO_PURPLE }} />
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Welcome back
            </Typography>
          </Box>

          <Typography variant="h1" sx={{
            fontFamily: 'Montserrat, sans-serif', fontWeight: 800,
            fontSize: { xs: '2.4rem', md: '3.6rem' }, lineHeight: 1.05,
            color: '#2C2C2C', mb: 3,
          }}>
            You found us early.
            <br />
            <Box component="span" sx={{ color: PRESSTO_PURPLE }}>Here&apos;s what we&apos;ve built since.</Box>
          </Typography>

          <Typography sx={{
            fontFamily: 'Lato, sans-serif', fontSize: { xs: '1.1rem', md: '1.25rem' },
            color: '#555', lineHeight: 1.7, mb: 6, maxWidth: 640, mx: 'auto',
          }}>
            Pressto is a structured writing platform for K-8 classrooms. Since you last visited,
            we&apos;ve added student newspapers, six research-based writing plans, an AI Writing Buddy,
            and curriculum-aligned lessons for hundreds of units. It&apos;s a lot. Here&apos;s the short version.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <CTAButton onClick={() => navigate('/register')}>Try it free →</CTAButton>
            <CTAButton dark={false} onClick={() => navigate('/login')}>Log back in</CTAButton>
          </Box>
        </Box>
      </Box>

      {/* What's new */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 9 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography sx={{
            fontFamily: 'Montserrat, sans-serif', fontWeight: 800,
            fontSize: '1.6rem', color: '#1e1e1e', mb: 1.5, textAlign: 'center',
          }}>
            What&apos;s new
          </Typography>
          <Typography sx={{
            fontFamily: 'Lato, sans-serif', fontSize: '1rem', color: '#666',
            lineHeight: 1.6, mb: 5, textAlign: 'center', maxWidth: 600, mx: 'auto',
          }}>
            We rebuilt Pressto from the ground up. Same belief — every student can write —
            but everything else is new.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
            {WHATS_NEW.map(f => (
              <Box key={f.title} sx={{
                border: '2px solid #1e1e1e', borderRadius: '8px',
                boxShadow: SHADOW_SM, p: 3, display: 'flex', gap: 2,
              }}>
                <Box component="span" sx={{ fontSize: '1.6rem', lineHeight: 1 }}>{f.icon}</Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1e1e1e', mb: 0.5 }}>{f.title}</Typography>
                  <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>{f.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Testimonials */}
      <TestimonialsSection
        testimonials={TESTIMONIALS.slice(0, 3)}
        title="What educators are saying"
      />

      {/* Bottom CTA */}
      <Box sx={{
        backgroundColor: '#2C2C2C', borderTop: '2px solid #1e1e1e',
        py: { xs: 8, md: 10 }, px: 3, textAlign: 'center',
      }}>
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h2" sx={{
            fontFamily: 'Montserrat, sans-serif', fontWeight: 800,
            fontSize: { xs: '1.7rem', md: '2.4rem' }, color: '#fff', mb: 2, lineHeight: 1.15,
          }}>
            Your students&apos; best writing<br />is still ahead of them.
          </Typography>
          <Typography sx={{
            fontFamily: 'Lato, sans-serif', fontSize: '1.05rem',
            color: 'rgba(255,255,255,0.7)', mb: 5,
          }}>
            Free to start. No credit card. Your first class is up in five minutes.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <CTAButton dark={false} onClick={() => navigate('/register')}>Start free →</CTAButton>
            <MuiButton
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem',
                textTransform: 'none', borderRadius: '6px', px: 4, py: 1.5,
                '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Log back in
            </MuiButton>
          </Box>
        </Box>
      </Box>
    </LandingLayout>
  );
}
